import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  MapContainer,
  TileLayer,
  Marker,
  Polyline,
  Circle,
  Popup,
  useMap,
} from 'react-leaflet';
import type { LatLngBoundsLiteral, Marker as LeafletMarker } from 'leaflet';
import type { GeoHistoryPoint } from '../api/hooks';
import { effectiveFix, useDevice, useDeviceLocationHistory } from '../api/hooks';
import { locationConfidence } from '../lib/locationConfidence';
import {
  addressWithoutVenue,
  fmtAgo,
  haversineMeters,
  humanisePlaceType,
} from '../components/format';

const OSM_TILES = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';
// Geoapify credit is required wherever its open-data-derived results are
// displayed (DE-170) — this page shows the resolved addresses and place names.
const OSM_ATTR =
  '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors' +
  ' · Addresses <a href="https://www.geoapify.com/">Geoapify</a>';

// Zoom level a table-row click settles on — close enough to read the street
// the device was on, without implying more precision than the fix has.
const FOCUS_ZOOM = 17;

// Alternatives beyond the best guess, rendered as a single muted line. Set to
// 4 = POI_LIMIT − 1, i.e. every candidate the resolver stored: when the fix
// can't distinguish between several nearby organisations, hiding one of them
// would be withholding exactly the ambiguity the operator needs to see. Each
// entry carries its distance so they can judge which is plausible.
const MAX_ALT_CANDIDATES = 4;

/**
 * The "possible companies" line: every candidate other than the one already
 * shown as the best guess. Returns null when there's nothing left to add.
 *
 * Filters by NAME rather than dropping index 0: the resolver prefers an
 * organisation over the building it sits in, so the best guess is not
 * necessarily the nearest candidate. In an office building like Kielo Growth
 * in Oulu the building itself is the 0 m entry while the tenant company is the
 * headline — and the building name is exactly what an operator wants to see
 * alongside it, so it must not be sliced away.
 */
function altCandidates(p: GeoHistoryPoint): string | null {
  const rest = (p.place_candidates ?? [])
    .filter((c) => c.name !== p.place_name)
    .slice(0, MAX_ALT_CANDIDATES);
  if (rest.length === 0) return null;
  return rest
    .map((c) => (c.distance_m == null ? c.name : `${c.name} (${c.distance_m} m)`))
    .join(' · ');
}

// Mirrors the DeviceTelemetryChart RANGES pattern. `daysAgo` drives the
// `since` ISO sent to the backend; an explicit "All" option opens the
// floor to the earliest geo_history partition we currently keep.
const RANGES: { key: string; label: string; daysAgo: number | 'all' }[] = [
  { key: '24h', label: '24 h', daysAgo: 1 },
  { key: '7d',  label: '7 d',  daysAgo: 7 },
  { key: '30d', label: '30 d', daysAgo: 30 },
  { key: '90d', label: '90 d', daysAgo: 90 },
  { key: '1y',  label: '1 y',  daysAgo: 365 },
  { key: 'all', label: 'All',  daysAgo: 'all' },
];

// Fit map to all markers whenever the data set changes. react-leaflet's
// MapContainer doesn't react to `bounds` / `center` after mount, but
// useMap() gives us the live map instance and `fitBounds()` re-aims it.
//
// The fit MUST live in an effect keyed on the bounds themselves, never in the
// render body: this page also polls useDevice() every 5 s, so a render-phase
// fitBounds() re-framed the map on every poll and threw away whatever zoom or
// pan the operator had just set — the map became impossible to explore.
// Keying on the serialised bounds means we re-aim only when the actual set of
// points changes (new fix, or a different range button), and otherwise leave
// the viewport entirely under the user's control.
function FitToBounds({ bounds }: { bounds: LatLngBoundsLiteral | null }) {
  const map = useMap();
  const boundsKey = bounds ? JSON.stringify(bounds) : '';
  useEffect(() => {
    if (bounds && bounds.length > 0) {
      map.fitBounds(bounds, { padding: [24, 24], maxZoom: 17 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boundsKey, map]);
  return null;
}

/**
 * Centre the map on a row picked from the table and open its popup.
 *
 * Keyed on `nonce` rather than the coordinates so re-clicking the same row
 * re-centres (after the operator has panned away) instead of being a no-op,
 * and so two rows at the same venue are still distinguishable clicks.
 *
 * Never zooms OUT: `max(currentZoom, FOCUS_ZOOM)` means picking a row from a
 * fleet-wide view zooms in to it, while picking one while already zoomed
 * further in just pans. The popup opens after the flight so Leaflet's
 * popup auto-pan doesn't fight the animation.
 */
function FlyToFocus({
  target,
  markerRefs,
}: {
  target: { index: number; point: GeoHistoryPoint; nonce: number } | null;
  markerRefs: React.MutableRefObject<Record<number, LeafletMarker | null>>;
}) {
  const map = useMap();
  useEffect(() => {
    if (!target) return;
    map.flyTo([target.point.lat, target.point.lon],
              Math.max(map.getZoom(), FOCUS_ZOOM), { duration: 0.6 });
    const timer = window.setTimeout(() => {
      markerRefs.current[target.index]?.openPopup();
    }, 650);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target?.nonce]);
  return null;
}

export function DeviceLocationHistory() {
  const { id } = useParams();
  const [rangeKey, setRangeKey] = useState('30d');
  const range = RANGES.find((r) => r.key === rangeKey) ?? RANGES[2]!;

  const sinceISO = useMemo(() => {
    if (range.daysAgo === 'all') return '1970-01-01T00:00:00Z';
    return new Date(Date.now() - range.daysAgo * 24 * 3600 * 1000).toISOString();
  }, [range]);

  const device = useDevice(id);
  const history = useDeviceLocationHistory(id, sinceISO);

  // Backend returns newest-first; for polyline + "previous distance"
  // calculation we want oldest-first.
  const rows = useMemo(() => {
    const raw = history.data?.points ?? [];
    return [...raw].sort((a, b) => new Date(a.ts).getTime() - new Date(b.ts).getTime());
  }, [history.data]);

  const shadowGeo = device.data?.device?.geo;
  // DE-281 — "current fix 53 d ago" was the page's only freshness signal, and
  // it reports when the position was ESTABLISHED, not when it was last
  // confirmed. Lead with the confirmation instead; the establishing age stays,
  // demoted, because on a history page it is genuinely interesting.
  const confidence = locationConfidence({
    geo: shadowGeo,
    online: device.data?.device?.online,
    lastSeen: device.data?.device?.last_seen,
  });

  // geo_history only gains a row when the device MOVES, so a unit that has sat
  // in one venue longer than the selected window has zero rows and the page
  // used to render nothing at all — the most common case (a stationary demo
  // unit) looked like a data outage. Fall back to the shadow's current fix so
  // "where is it now" is always answered; the map/table code below is reused
  // unchanged by synthesising a single point from it.
  const currentFix = useMemo(() => effectiveFix(shadowGeo), [shadowGeo]);
  const shadowPoint = useMemo<GeoHistoryPoint | null>(() => {
    if (rows.length > 0 || !currentFix) return null;
    const ts = currentFix.timestamp;
    return {
      ts: ts != null ? new Date(Number(ts) * 1000).toISOString() : new Date().toISOString(),
      lat: currentFix.lat,
      lon: currentFix.lon,
      accuracy_m: currentFix.accuracy,
      source: currentFix.source,
      address: currentFix.address,
      place_name: currentFix.place_name,
      // Only geo_history rows carry these; the shadow doesn't mirror them.
      place_type: null,
      place_candidates: null,
    };
  }, [rows.length, currentFix]);

  const points = shadowPoint ? [shadowPoint] : rows;
  const showingCurrentOnly = shadowPoint !== null;

  const bounds = useMemo<LatLngBoundsLiteral | null>(() => {
    if (points.length === 0) return null;
    return points.map((p) => [p.lat, p.lon]) as LatLngBoundsLiteral;
  }, [points]);

  // Distance from previous fix, in metres, indexed by point.
  const deltas = useMemo(() => {
    return points.map((p, i) => {
      const prev = points[i - 1];
      if (!prev) return null;
      return haversineMeters(prev.lat, prev.lon, p.lat, p.lon);
    });
  }, [points]);

  const newest = points[points.length - 1];

  // Table-row → map focus. `nonce` makes a repeat click on the same row
  // re-centre rather than no-op; the refs let the click open that marker's
  // popup, so the row and the pin show the same thing.
  const [focus, setFocus] =
    useState<{ index: number; point: GeoHistoryPoint; nonce: number } | null>(null);
  const nonceRef = useRef(0);
  const markerRefs = useRef<Record<number, LeafletMarker | null>>({});
  const focusRow = (index: number, point: GeoHistoryPoint) => {
    nonceRef.current += 1;
    setFocus({ index, point, nonce: nonceRef.current });
  };
  // Drop a stale selection when the window changes — the row it pointed at is
  // gone, and MapContainer remounts (so the marker refs are stale too).
  useEffect(() => {
    setFocus(null);
    markerRefs.current = {};
  }, [rangeKey, id]);

  return (
    <div className="space-y-4">
      <div className="flex items-baseline justify-between">
        <div>
          <Link
            to={`/devices/${encodeURIComponent(id ?? '')}`}
            className="text-xs text-accent hover:underline"
          >
            ← back to device
          </Link>
          <h1 className="text-xl font-semibold tracking-tight font-mono">{id}</h1>
          <p className="text-sm text-muted">
            Location history
            {shadowGeo && (
              <>
                {' · '}
                <span
                  className={confidence.tone === 'warn' ? 'text-warn' : undefined}
                  title={confidence.explanation}
                >
                  {confidence.headline}
                </span>
                {' · '}±{Math.round(shadowGeo.accuracy ?? 0)} m
                {confidence.established && <>{' · '}{confidence.established}</>}
                {(shadowGeo.place_name || shadowGeo.address) && (
                  <>{' · '}{shadowGeo.place_name ?? shadowGeo.address}</>
                )}
              </>
            )}
          </p>
        </div>
        <div className="flex gap-1">
          {RANGES.map((r) => (
            <button
              key={r.key}
              onClick={() => setRangeKey(r.key)}
              className={
                'px-2 py-1 rounded-md text-xs ' +
                (r.key === rangeKey
                  ? 'bg-accent text-white'
                  : 'text-muted hover:text-fg hover:bg-soft')
              }
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      <div className="card overflow-hidden p-0">
        {history.isLoading && (
          <p className="text-sm text-muted p-4">Loading…</p>
        )}
        {history.data && points.length === 0 && (
          <p className="text-sm text-muted p-4">
            No location fixes recorded in this window, and no current position
            reported yet. A fix appears once the device completes a location
            scan.
          </p>
        )}
        {showingCurrentOnly && (
          <p className="text-[11px] text-muted px-4 pt-3">
            The device hasn't moved during this window — showing its current
            reported location. History rows are only written when a device
            changes location, so try a longer range to see earlier venues.
          </p>
        )}
        {points.length > 0 && bounds && (
          <MapContainer
            // Re-key on range/id/point-count so MapContainer remounts on
            // window change — bounds-fitting then works on the fresh map.
            key={`${id}-${rangeKey}-${points.length}`}
            bounds={bounds}
            boundsOptions={{ padding: [24, 24], maxZoom: 17 }}
            scrollWheelZoom
            style={{ height: 460, width: '100%' }}
          >
            <TileLayer url={OSM_TILES} attribution={OSM_ATTR} />
            <FitToBounds bounds={bounds} />
            <FlyToFocus target={focus} markerRefs={markerRefs} />
            <Polyline
              positions={points.map((p) => [p.lat, p.lon])}
              pathOptions={{ color: '#2563eb', weight: 2, opacity: 0.7 }}
            />
            {points.map((p, i) => {
              const isNewest = i === points.length - 1;
              const isOldest = i === 0;
              return (
                <Marker
                  key={`${p.ts}-${i}`}
                  position={[p.lat, p.lon]}
                  ref={(m) => { markerRefs.current[i] = m; }}
                >
                  <Popup>
                    <div className="text-xs">
                      {(p.place_name ?? humanisePlaceType(p.place_type)) && (
                        <div
                          className={
                            p.place_name ? 'font-medium' : 'font-medium text-muted'
                          }
                        >
                          {p.place_name ?? humanisePlaceType(p.place_type)}
                        </div>
                      )}
                      {addressWithoutVenue(p.address, p.place_name) && (
                        <div>{addressWithoutVenue(p.address, p.place_name)}</div>
                      )}
                      {altCandidates(p) && (
                        <div className="text-muted">also nearby: {altCandidates(p)}</div>
                      )}
                      <div>{new Date(p.ts).toLocaleString()}</div>
                      <div>
                        {p.lat.toFixed(5)}, {p.lon.toFixed(5)}
                      </div>
                      <div>±{Math.round(p.accuracy_m)} m{p.source ? ` · ${p.source}` : ''}</div>
                      {deltas[i] !== null && (
                        <div className="text-muted">moved {Math.round(deltas[i]!)} m from previous</div>
                      )}
                      {isNewest && (
                        <div className="text-accent">
                          {showingCurrentOnly ? 'current location' : 'newest'}
                        </div>
                      )}
                      {isOldest && points.length > 1 && (
                        <div className="text-muted">oldest in window</div>
                      )}
                    </div>
                  </Popup>
                </Marker>
              );
            })}
            {newest && (
              <Circle
                center={[newest.lat, newest.lon]}
                radius={newest.accuracy_m}
                pathOptions={{ color: '#2563eb', weight: 1, fillOpacity: 0.06 }}
              />
            )}
          </MapContainer>
        )}
      </div>

      {points.length > 0 && (
        <div className="card overflow-x-auto">
          <p className="text-[11px] text-muted pb-2">
            Select a row to centre the map on that location.
          </p>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-muted border-b border-border">
                <th className="font-medium py-2 pr-4">Time</th>
                <th className="font-medium py-2 pr-4">Location</th>
                <th className="font-medium py-2 pr-4">Lat</th>
                <th className="font-medium py-2 pr-4">Lon</th>
                <th className="font-medium py-2 pr-4">Accuracy</th>
                <th className="font-medium py-2 pr-4">Source</th>
                <th className="font-medium py-2 pr-4">From previous</th>
              </tr>
            </thead>
            <tbody>
              {[...points].reverse().map((p, idxRev) => {
                const i = points.length - 1 - idxRev;
                const d = deltas[i];
                const isFocused = focus?.index === i;
                return (
                  // Keyboard-reachable as well as clickable: this is the only
                  // way to aim the map at a specific fix, so it shouldn't be
                  // mouse-only.
                  <tr
                    key={`${p.ts}-${i}`}
                    onClick={() => focusRow(i, p)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        focusRow(i, p);
                      }
                    }}
                    tabIndex={0}
                    role="button"
                    aria-label={`Centre map on ${p.place_name ?? p.address ?? 'this location'}`}
                    title="Centre the map on this location"
                    className={
                      'border-b border-border last:border-0 cursor-pointer ' +
                      (isFocused ? 'bg-accent/10' : 'hover:bg-soft')
                    }
                  >
                    <td className="py-2 pr-4 tabular-nums">
                      <div>{new Date(p.ts).toLocaleString()}</div>
                      <div className="text-xs text-muted">{fmtAgo(p.ts)}</div>
                    </td>
                    <td className="py-2 pr-4 max-w-xs">
                      {(p.place_name ?? humanisePlaceType(p.place_type)) && (
                        <div
                          className={
                            p.place_name ? 'font-medium' : 'font-medium text-muted'
                          }
                        >
                          {p.place_name ?? humanisePlaceType(p.place_type)}
                        </div>
                      )}
                      <div
                        className={
                          p.place_name || p.place_type ? 'text-xs text-muted' : ''
                        }
                      >
                        {addressWithoutVenue(p.address, p.place_name) ??
                          (p.place_name || p.place_type ? '' : '—')}
                      </div>
                    </td>
                    <td className="py-2 pr-4 font-mono text-xs tabular-nums">{p.lat.toFixed(5)}</td>
                    <td className="py-2 pr-4 font-mono text-xs tabular-nums">{p.lon.toFixed(5)}</td>
                    <td className="py-2 pr-4 tabular-nums">±{Math.round(p.accuracy_m)} m</td>
                    <td className="py-2 pr-4">{p.source ?? '—'}</td>
                    <td className="py-2 pr-4 tabular-nums">
                      {d == null ? '—' : `${Math.round(d)} m`}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
